import React, { useState, useEffect } from 'react';
import { Video, Copy, Check, Pencil, AlertTriangle } from 'lucide-react';
import api from '../api';

const MeetNamePrompt = ({ meetJoinUrl, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('users/profile/')
      .then(res => {
        setFirstName(res.data.first_name || '');
        setLastName(res.data.last_name || '');
        setEditing(!res.data.first_name?.trim() || !res.data.last_name?.trim());
      })
      .catch(() => setError('No se pudieron cargar tus datos personales.'))
      .finally(() => setLoading(false));
  }, []);

  const fullName = `${firstName} ${lastName}`.trim();

  const handleCopy = () => {
    navigator.clipboard.writeText(fullName);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError('Completá nombre y apellido.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await api.post('users/profile/', { first_name: firstName, last_name: lastName });
      setEditing(false);
    } catch {
      setError('No se pudieron guardar los datos. Intentá de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = () => {
    window.open(meetJoinUrl, '_blank', 'noreferrer');
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 11000 }}>
      <div
        className="modal-content glassmorphism animate-fade-in"
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
          <h5 className="modal-title section-title fs-5 mb-0 d-flex align-items-center gap-2">
            <Video size={22} className="text-success" /> Antes de entrar a Meet
          </h5>
          <button className="btn-close" onClick={onClose} />
        </div>

        <div className="p-4 pt-3">
          {loading ? (
            <div className="text-center py-4 text-muted">Cargando tus datos...</div>
          ) : editing ? (
            <form onSubmit={handleSave}>
              <p className="text-muted small mb-3">
                Tu nombre y apellido no están completos en el sistema. Completalos para poder identificarte en Meet.
              </p>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label fw-semibold">Nombre</label>
                  <input
                    type="text"
                    className="form-control"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold">Apellido</label>
                  <input
                    type="text"
                    className="form-control"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>
              {error && <div className="alert alert-danger py-2 small">{error}</div>}
              <button type="submit" className="btn btn-primary w-100 fw-bold" disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar datos'}
              </button>
            </form>
          ) : (
            <>
              <p className="text-muted small mb-3">
                Este es el nombre registrado en el sistema. Copialo y pegalo como tu nombre en Google Meet
                antes de unirte a la clase.
              </p>

              <div className="d-flex align-items-center justify-content-between gap-2 p-3 mb-3 rounded border bg-light">
                <div className="fw-bold fs-5">{fullName}</div>
                <button
                  type="button"
                  className={`btn btn-sm ${copied ? 'btn-success' : 'btn-outline-primary'} d-flex align-items-center gap-1`}
                  onClick={handleCopy}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copiado' : 'Copiar'}
                </button>
              </div>

              <div className="alert alert-warning d-flex align-items-start gap-2 small py-2 mb-3">
                <AlertTriangle size={16} className="flex-shrink-0 mt-1" />
                <div>
                  Escribí este nombre <strong>exactamente así</strong> al entrar a Meet. Si no coincide,
                  no se contabiliza tu tiempo de conexión.
                </div>
              </div>

              <div className="alert alert-info small py-2 mb-3">
                <strong>¿Ya tenés sesión de Google iniciada?</strong> Meet va a usar el nombre de esa
                cuenta directamente y no te va a dejar pegar el de arriba. Para que te pida el nombre:
                <ul className="mb-0 mt-1 ps-3">
                  <li>Abrí el link en una ventana de <strong>incógnito</strong> (o cerrá sesión de Google
                    antes de entrar), o</li>
                  <li>Si siempre usás la misma cuenta de Google para las clases, cambiá el nombre de esa
                    cuenta en <strong>myaccount.google.com</strong> para que coincida con el de arriba —
                    así Meet lo va a mostrar bien automáticamente, siempre.</li>
                </ul>
              </div>

              <button
                type="button"
                className="btn btn-link btn-sm text-muted d-flex align-items-center gap-1 mb-3 p-0"
                onClick={() => setEditing(true)}
              >
                <Pencil size={14} /> Mis datos están mal, editar
              </button>

              {error && <div className="alert alert-danger py-2 small">{error}</div>}

              <button
                type="button"
                className="btn w-100 py-3 fw-bold border-0 text-white"
                style={{ background: 'linear-gradient(135deg, #10b981, #059669)', borderRadius: '12px' }}
                onClick={handleContinue}
              >
                Continuar a Google Meet
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetNamePrompt;
